import type { JSX } from 'react';

import clsx from 'clsx';

import { testIds } from '@/components/constants/testIds';
import { composeFontFamilyClasses } from '@/components/functions/composeFontFamilyClasses';
import { composeFontWeightClasses } from '@/components/functions/composeFontWeightClasses';
import { composeTextAlignmentClasses } from '@/components/functions/composeTextAlignmentClasses';
import { composeTextColorClasses } from '@/components/functions/composeTextColorClasses';
import { composeTextDecorationClasses } from '@/components/functions/composeTextDecorationClasses';
import { composeTextOverflowClasses } from '@/components/functions/composeTextOverflowClasses';
import { composeTextSizeClasses } from '@/components/functions/composeTextSizeClasses';
import { composeTextTransformClasses } from '@/components/functions/composeTransformClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Color } from '@/components/types/Color';
import type { FontFamily } from '@/components/types/FontFamily';
import type { FontWeight } from '@/components/types/FontWeight';
import type { TextAlignment } from '@/components/types/TextAlignment';
import type { TextDecoration } from '@/components/types/TextDecoration';
import type { TextElement } from '@/components/types/TextElement';
import type { TextOverflow } from '@/components/types/TextOverflow';
import type { TextSize } from '@/components/types/TextSize';
import type { TextTransform } from '@/components/types/TextTransform';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';

type TextProps = {
    children: React.ReactNode | string;
    as?: TextElement;
    family?: Adaptive<FontFamily>;
    size?: Adaptive<TextSize>;
    weight?: Adaptive<FontWeight>;
    color?: Color;
    dynamicColor?: string;
    alignment?: Adaptive<TextAlignment>;
    width?: Adaptive<Width>;
    transform?: Adaptive<TextTransform>;
    decoration?: Adaptive<TextDecoration>;
    overflow?: Adaptive<TextOverflow>;
    className?: string;
    testId?: string;
};

export default function Text({
    children,
    as: Component = 'p',
    family,
    size = componentsConfig.text.size,
    weight = componentsConfig.text.weight,
    color = componentsConfig.text.color,
    dynamicColor,
    alignment = componentsConfig.text.alignment,
    width = componentsConfig.text.width,
    transform,
    decoration,
    overflow,
    className,
    testId = testIds.text.base,
}: TextProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <Component
            data-testid={testId}
            className={clsx(
                composeTextSizeClasses(size),
                composeFontWeightClasses(weight),
                composeTextColorClasses(color),
                composeTextAlignmentClasses(alignment),
                composeWidthClasses(width),
                family && composeFontFamilyClasses(family),
                overflow && composeTextOverflowClasses(overflow),
                transform && composeTextTransformClasses(transform),
                decoration && composeTextDecorationClasses(decoration),
                className
            )}
            style={{ color: dynamicColor }}
        >
            {children}
        </Component>
    );
}
